import {action, observable, computed} from 'mobx';
import {message} from '@enos/dpl';
import _ from 'lodash';
import localeText, {getLocale} from '@locale';
import axiosInstance from '~/utils/axiosInstance';
import uris from '~/uris';
import Structure from './models/Structure';
import Asset from './models/Asset';
import User from './models/User';
import {transformVarToKey, transformKeyToVar} from '~/utils/tools';

export class StructureStore {
  @observable structures = [];

  @observable isLoading = true;

  @observable structureDetail = {};

  @observable structureTree = [];

  @observable leafStructure = {};

  @observable currentStructure = {};

  @observable currentStructureId = '';

  @observable roleAsStructureTree = false;

  @observable initStructureTargetKeys = [];

  @observable structureTargetKeys = [];

  @observable structureAssets = [];

  @observable assginedUserList = [];

  @observable syncing = false;

  @observable assignedAssets = [];

  @observable initCurrentValue = {}

  @observable currentTag = {}

  @observable structureTagList = []

  @observable tagTotal = 0;

  @observable targetLiId = '';

  @observable selectedLiLoading = false;

  @observable orderedStructureTree = {};

  @observable initOrderedStructureTree = {};

  @observable getTreeLoading = false;

  @computed get currentStructureName() {
    return transformVarToKey(this.structureDetail.nameJson);
  }

  @action.bound
  setSelected(id) {
    this.targetLiId = id;
  }

  @action.bound
  setSelectedLiLoading(boolean) {
    this.selectedLiLoading = boolean;
  }

  @action.bound
  setInitStructureTree() {
    this.setStructureTree(null, [this.updateStructureTreeFromServer(this.structureDetail)]);
  }

  @action.bound
  setRoleAsStructureTree(boolean) {
    this.roleAsStructureTree = boolean;
  }

  @action.bound
  setStructure(data) {
    this.structures = data;
  }

  @action.bound
  clearStructureDetail() {
    this.structureDetail = {};
  }

  @action.bound
  resetLoading() {
    this.isLoading = false;
  }

  @action.bound
  setCurrentStructure(data) {
    this.currentStructure = data;
  }

  @action.bound
  setCurrentStructureId(id) {
    this.currentStructureId = id;
  }

  @action.bound
  setLeafStructure({id, name}) {
    this.leafStructure = {id, name};
  }

  @action.bound
  setStructureTargetKeys(keys) {
    this.structureTargetKeys = keys;
  }

  @action.bound
  setInitStructureTargetKeys(keys) {
    this.initStructureTargetKeys = keys;
  }

  @action.bound
  setAssets(data) {
    this.structureAssets = data;
  }

  @action.bound
  setStructureAssignedAsset(data) {
    this.assignedAssets = data;
  }

  @action.bound
  setInitStructureAssignedAsset(data) {
    this.initAssignedAssets = data;
  }

  @action.bound
  setAssignedUserList(data) {
    this.assginedUserList = data;
  }

  @action.bound
  setInitCurrentValue(obj) {
    this.initCurrentValue = obj;
  }

  @action.bound
  resetSyncStatus() {
    this.syncing = false;
  }

  @action.bound
  resetChildren(parentId) {
    const parent = this.findStructureInStructureTree(parentId, this.structureTree);
    if (parent) {
      parent.children = [];
    }
  }

  @action.bound
  deleteStructureFromSever(structureId) {
    const child = this.findStructureInStructureTree(structureId, this.structureTree, null);
    const parent = this.findStructureInStructureTree(child.parentId, this.structureTree, null);
    _.remove(parent.children, ({id}) => id === structureId);
    if (parent.children.length === 0) {
      parent.isLeaf = true;
    }
  }

  @action.bound
  findStructureInStructureTree(id, data, preStructure) {
    let structure = preStructure || null;
    data.some((item) => {
      if (item.id === id) {
        structure = item;
        return true;
      }

      if (item.children && item.children.length > 0) {
        structure = this.findStructureInStructureTree(id, item.children, structure);
        return false;
      }
      return false;
    });
    return structure;
  }

  @action.bound
  setStructureTree(parentId, data) {
    const parent = this.findStructureInStructureTree(parentId, this.structureTree, null);
    if (parent) {
      data.forEach((l) => {
        parent.children.push(l);
      });
    } else {
      this.structureTree = data;
    }
  }

  @action.bound
  getStructureList() {
    this.isLoading = true;
    return axiosInstance
      .post(uris.getStructureList())
      .then((data) => {
        this.setStructure(data.structures.map(json => this.updateStructureFromServer(json)));
        this.resetLoading();
      })
      .catch(() => {
        this.resetLoading();
      });
  }

  @action.bound
  createStructure(parentId = '', name) {
    axiosInstance
      .post(
        uris.createStructure(),
        {
          parentId,
          nameJson: _.merge(transformKeyToVar(name), {default: name[getLocale()]})
        }
      )
      .then((data) => {
        if (parentId) {
          message.success(localeText('STRUCTURE.PRMTMSG_ADD_CHILD_SUCCEED'));
          this.createChildrenFromSever(parentId, data);
        } else {
          message.success(localeText('STRUCTURE.PRMTMSG_ADD_SUCCEED'));
          this.getStructureList();
        }
        this.resetLoading();
      })
      .catch(() => {
        this.resetLoading();
      });
  }

  @action.bound
  createChildrenFromSever(parentId, data) {
    const parent = this.findStructureInStructureTree(parentId, this.structureTree, null);
    if (parent.children.length === 0) {
      parent.isLeaf = false;
      this.structureDetail.isLeaf = false;
    }
    this.setStructureTree(parentId, [this.updateStructureTreeFromServer(data)]);
  }

  @action.bound
  updateStructureFromServer(json) {
    let structure = this.structures.find(({id}) => id === json.id);
    if (!structure) {
      structure = new Structure(this, json.id);
    }
    structure.updateFromJson(json);
    return structure;
  }

  @action.bound
  deleteStructure(structureId) {
    axiosInstance
      .post(uris.deleteStructure(), null, {
        params: {
          id: structureId
        },
        showError: {
          showPopup: true,
          title: localeText('COMMON.TITLE_DELETE_FAIL')
        }
      })
      .then(() => {
        if (this.roleAsStructureTree) {
          this.deleteStructureFromSever(structureId);
        } else {
          this.getStructureList();
        }
        message.success(localeText('STRUCTURE.DELETE_STRUCTURE_SUCCEED'));
      })
      .catch(() => {
        this.resetLoading();
      });
  }

  @action.bound
  async getStructureDetail(structureId) {
    this.isLoading = true;
    await axiosInstance
      .get(uris.structureDetail(), {
        params: {
          id: structureId
        }
      })
      .then((data) => {
        this.clearStructureDetail();
        this.resetLoading();
        this.updateStructureDetailFromServer(data);
      });
  }

  @action.bound
  updateStructureDetailFromServer(data) {
    const structureDetail = new Structure(this, data.id);
    this.structureDetail = structureDetail;
    structureDetail.updateStructureDetailFromJson(data);
  }

  @action.bound
  updateStructureDetailName(data) {
    this.structureDetail.name = data;
  }

  @action.bound
  setStructureStatus(structureId, isAutoDispatch) {
    axiosInstance
      .post(uris.setStructureStatus(), {
        id: structureId,
        isAutoDispatch: !isAutoDispatch
      })
      .then(() => {
        this.updateSturctureStatusFromServer(structureId, isAutoDispatch);
      });
  }

  @action.bound
  updateSturctureStatusFromServer(structureId, isAutoDispatch) {
    const sturcture = this.structures.find(({id}) => id === structureId);
    sturcture.isAutoDispatch = !isAutoDispatch;
  }

  @action.bound
  getStructureChildren(structureId) {
    this.isLoading = true;
    return axiosInstance
      .get(uris.getStructureChildren(), {
        params: {
          id: structureId
        }
      })
      .then((data) => {
        this.setStructureTree(structureId, data.children.map(json => this.updateStructureTreeFromServer(json)));
        this.resetLoading();
      });
  }

  @action.bound
  updateStructureTreeFromServer(json) {
    let structure = this.structureTree.find(({id}) => id === json.id);
    if (!structure) {
      structure = new Structure(this, json.id);
    }
    structure.updateStructureTreeFromJson(json);
    return structure;
  }

  @action.bound
  getCompleteStructureList() {
    this.isLoading = true;
    return axiosInstance
      .get(uris.getCompleteStructureList())
      .then((data) => {
        this.resetLoading();
        this.updateWholeTree(data.structures);
        return data;
      })
      .catch(() => {
        this.resetLoading();
      });
  }

  @action.bound
  assignUserStructures(userId, structureIds) {
    this.isLoading = true;
    return axiosInstance.post(uris.assignUserStructures(), {
      userId,
      structureIds
    });
  }

  @action.bound
  getStructureAssetList(structureId) {
    this.setSelectedLiLoading(true);
    axiosInstance
      .post(uris.getStructureAssetList(), null, {
        params: {structureId}
      })
      .then((data) => {
        const keys = [];
        const value = [];
        data.assets.forEach((a) => {
          if (a.isAssigned) {
            keys.push(a.id);
            value.push(a);
          }
        });
        this.setStructureTargetKeys(keys);
        this.setStructureAssignedAsset(value);
        this.setInitStructureAssignedAsset(value);
        this.setInitStructureTargetKeys(keys);
        this.setAssets(data.assets.map(json => this.updateStructureAssetFromServer(json)));
        this.setSelectedLiLoading(false);
      })
      .catch(() => {
        this.setSelectedLiLoading(false);
      });
  }

  @action.bound
  updateStructureAssetFromServer(json) {
    let structureAsset = this.structureAssets.find(({id}) => id === json.id);
    if (!structureAsset) {
      structureAsset = new Asset(this, json.id);
    }
    structureAsset.updateFromJson(json);
    return structureAsset;
  }

  @action.bound
  assignAssetForStructure(structureId, assetIds) {
    this.isLoading = true;
    axiosInstance
      .post(uris.assignAssetForStructure(), {
        structureId,
        assetIds
      })
      .then(() => {
        this.getStructureAssetList(structureId);
        this.handleHasAssetFromSever(structureId);
      })
      .then(() => {
        message.success(localeText('STRUCTURE.PRMTMSG_ASSIGN_SUCCEED'));
        this.resetLoading();
      })
      .catch(() => {
        this.resetLoading();
      });
  }

  @action.bound
  getStructureUserAssignedList(structureId) {
    this.isLoading = true;
    axiosInstance
      .post(uris.getStructureUserAssignedList(), null, {
        params: {structureId}
      })
      .then((data) => {
        this.setAssignedUserList(data.users.map(json => this.updateUserFromServer(json)));
        this.resetLoading();
      })
      .catch(() => {
        this.resetLoading();
      });
  }

  @action.bound
  updateUserFromServer(json) {
    let assginedUser = this.assginedUserList.find(({id}) => id === json.id);
    if (!assginedUser) {
      assginedUser = new User(this, json.id);
    }

    assginedUser.updateFromJson(json);
    return assginedUser;
  }

  @action.bound
  syncAssets() {
    this.syncing = true;
    axiosInstance
      .post(uris.syncAssets())
      .then(() => {
        message.success(localeText('STRUCTURE.PRMTMSG_SYNC_SUCCEED'));
        this.resetSyncStatus();
        this.resetLoading();
      })
      .catch(() => {
        this.resetSyncStatus();
        this.resetLoading();
      });
  }

  @action.bound
  modifyStructureName(id, name) {
    this.isLoading = true;
    axiosInstance
      .post(
        uris.modifyStructureName(),
        {
          id,
          nameJson: _.merge(transformKeyToVar(name), {default: name[getLocale()]})
        },
        {
          showError: {
            showPopup: true,
            title: localeText('COMMON.TITLE_MODIFY_FAIL')
          }
        }
      )
      .then((json) => {
        this.updateStructureNameFromServer(json);
        message.success(localeText('STRUCTURE.PRMTMSG_MODIFY_SUCCEED'));
        this.resetLoading();
      })
      .catch(() => {
        this.resetLoading();
      });
  }

  @action.bound
  updateStructureNameFromServer(json) {
    const structure = this.findStructureInStructureTree(json.id, this.structureTree, null);
    structure.name = json.name;
  }

  @action.bound
  checkNameExist(name, cb, locale, rootId, id) {
    axiosInstance
      .post(uris.checkNameExist(), null, {
        params: {type: 'structure', name, rootId, id, locale},
        showError: {isShow: false}
      })
      .then((data) => {
        if (!data) {
          cb(false);
        } else {
          cb();
        }
      })
      .catch((error) => {
        cb(error.message);
      });
  }

  @action.bound
  setNameMessage(msg) {
    this.nameMessage = msg;
  }

  @action.bound
  async handleHasAssetFromSever(structureId) {
    await this.getStructureDetail(structureId);
    const structure = this.findStructureInStructureTree(structureId, this.structureTree, null);
    this.updateStructureHasAsset(structure);
  }

  @action.bound
  updateStructureHasAsset(structure) {
    structure.hasAsset = this.structureDetail.hasAsset;
  }

  @action('更新整个树，建立树结构')
  updateWholeTree(structures, parentId = null) {
    this.setStructureTree(parentId, (structures || []).map(json => this.updateStructureTreeFromServer(json)));
    (structures || []).forEach(({id, children}) => this.updateWholeTree(children, id));
  }

  @action.bound
  setCurrentTag(tag) {
    this.currentTag = tag;
    // console.log(this.currentTag,'')
  }

  @action.bound
  createTag(structureId, tag) {
    this.setSelectedLiLoading(true);
    axiosInstance
      .post(uris.createTag(), {
        structureId,
        tag
      })
      .then(() => {
        message.success(localeText('STRUCTURE.PRMTMSG_CREATE_TAG_SUCCEED'));
        this.getStructureTagList(this.targetLiId);
        this.setSelectedLiLoading(false);
      })
      .catch(() => {
        this.setSelectedLiLoading(false);
      });
  }

  @action.bound
  modifyTag(id, key, value) {
    this.setSelectedLiLoading(true);
    axiosInstance
      .post(uris.modifyTag(), {
        id,
        key,
        value
      })
      .then(() => {
        message.success(localeText('STRUCTURE.PRMTMSG_MODIFY_TAG_SUCCEED'));
        this.getStructureTagList(this.targetLiId);
        this.setSelectedLiLoading(false);
      })
      .catch(() => {
        this.setSelectedLiLoading(false);
      });
  }

  @action.bound
  getStructureTagList(structureId) {
    this.isLoading = true;
    return axiosInstance
      .get(uris.getStructureTagList(), {
        params: {
          structureId
        }
      })
      .then((data) => {
        this.setStructureTagList(data.tags);
        this.setTagTotal(data.total);
        this.resetLoading();
      })
      .catch(() => {
        this.resetLoading();
      });
  }

  @action.bound
  setStructureTagList(data) {
    this.structureTagList = data;
  }

  @action.bound
  setTagTotal(total) {
    this.tagTotal = total;
  }

  @action.bound
  checkTagKeyExist(key, id, cb) {
    axiosInstance
      .post(uris.checkTagKeyExist(), null, {
        params: {key, id: id || undefined, structureId: this.targetLiId},
        showError: {isShow: false}
      })
      .then((data) => {
        if (!data) {
          cb(false);
        } else {
          cb();
        }
      })
      .catch((error) => {
        cb(error.message);
      });
  }

  @action.bound
  deleteStructureTag(id) {
    this.setSelectedLiLoading(true);
    return axiosInstance
      .post(uris.deleteStructureTag(), {id})
      .then(() => {
        message.success(localeText('STRUCTURE.MSG_DELETE_STRUCTURE_TAG_SUCCEED'));
        this.getStructureTagList(this.targetLiId);
        this.setSelectedLiLoading(false);
      });
  }

  @action.bound
  setGetTreeLoading(boolean) {
    this.getTreeLoading = boolean;
  }

  @action.bound
  setOrderedStructureTree(data) {
    this.orderedStructureTree = data;
  }

  @action.bound
  clearOrderedStructureTree() {
    this.orderedStructureTree = {};
  }

  @action.bound
  setInitOrderedStructureTree(data) {
    this.initOrderedStructureTree = data;
  }

  @action.bound
  getCompleteStructureOrdered(id) {
    this.setGetTreeLoading(true);
    return axiosInstance
      .get(uris.getCompleteStructureOrdered(), {
        params: {
          id
        }
      })
      .then((data) => {
        this.setGetTreeLoading(false);
        this.setOrderedStructureTree(data.structure);
        this.setInitOrderedStructureTree(data.structure);
      })
      .catch(() => {
        this.setGetTreeLoading(false);
      });
  }

  @action.bound
  modifyStructureOrder(body) {
    this.isLoading = true;
    axiosInstance
      .post(uris.modifyStructureOrder(), {
        ...body
      })
      .then(() => {
        message.success(localeText('STRUCTURE.MSG_SORT_SUCCESS'));
        this.getStructureChildren(body.id);
        this.resetLoading();
      })
      .catch(() => {
        this.resetLoading();
      });
  }

  @action.bound
  modifyAssetOrder(body) {
    this.setSelectedLiLoading(true);
    axiosInstance
      .post(uris.modifyAssetOrder(), {
        ...body
      })
      .then(() => {
        this.getStructureAssetList(body.id);
        this.setSelectedLiLoading(false);
      })
      .catch(() => {
        this.setSelectedLiLoading(false);
      });
  }

  @action.bound
  checkIsRelated(appId, structures) {
    return axiosInstance
      .post(uris.checkIsRelated(), {appId, structures});
  }
}

export default new StructureStore();
